import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import { isEmpty, REGEX } from '@utils/validationUtils';

import { filterPersonLovsById } from './utils';
import { IPersonDetailsDrawerInfo } from '.';

export const initialValues: IPersonDetailsDrawerInfo = {
  userId: '',
  personId: '',
  type: '',
  companyName: '',
  title: '',
  titleId: '',
  firstName: '',
  middleName: '',
  lastName: '',
  maidenName: '',
  initials: '',
  dateOfBirth: '',
  nationalityCode: '',
  email: '',
  phoneType: '',
  phoneNumber: '',
  relativeOfCode: '',
  smoker: 'NO',
  weight: 0,
  height: 0,
  dateOfDeath: '',
  dateOfDeathNotified: '',
  taxable: false,
  financialReference: '',
  occupationClass: '',
  occupationClassCode: '',
  profession: '',
  employer: '',
  dateOfEmployment: '',
  salary: 0,
  dateOfSelfCertificateReceived: '',
  dateOfSelfCertificateValidated: '',
  category: '',
  dateOfKYC: '',
  comments: '',
  managerName: '',
  relation: '',
  taxResidenceCountry: '',
  remarks: '',
  registrationPlace: '',
  registrationDate: '',
  legalRepresentativeFullName: '',
  legalRepresentativeDateOfBirth: '',
  legalRepresentativeNationality: '',
  mainBusinessPartner: {
    personId: ''
  },
  introducedBy: {
    id: ''
  }
};

export const getSections = (
  data: IPersonDetailsDrawerInfo,
  lists: Record<string, Record<string, string>>
): IDynamicFormWidgetSection[] => {
  const isCompany = data?.type?.toUpperCase() === 'COMPANY';
  const isPerson = data?.type?.toUpperCase() === 'INDIVIDUAL';

  return [
    {
      sectionTitle: 'General Details',
      inputs: {
        type: {
          name: 'type',
          title: 'Type',
          type: FormFieldType.select,
          placeholder: 'Type',
          value: data?.type,
          required: true,
          selectOptions: lists?.personType,
          disabled: true
        },
        title: {
          name: 'title',
          title: 'Title',
          type: FormFieldType.select,
          placeholder: 'Title',
          value: data?.title,
          required: isPerson,
          hidden: !isPerson,
          disabled: !isPerson,
          selectOptions: lists?.titles
        },
        firstName: {
          name: 'firstName',
          title: 'First Name',
          type: FormFieldType.text,
          placeholder: 'First Name',
          value: data?.firstName,
          required: isPerson,
          hidden: !isPerson,
          disabled: !isPerson,
          minCharacters: 2,
          maxCharacters: 100
        },
        middleName: {
          name: 'middleName',
          title: 'Middle Name',
          type: FormFieldType.text,
          placeholder: 'Middle Name',
          value: data?.middleName,
          required: isPerson,
          hidden: !isPerson,
          disabled: !isPerson,
          minCharacters: 1,
          maxCharacters: 100
        },
        lastName: {
          name: 'lastName',
          title: 'Last Name',
          type: FormFieldType.text,
          placeholder: 'Last Name',
          value: data?.lastName,
          required: isPerson,
          hidden: !isPerson,
          disabled: !isPerson,
          minCharacters: 2,
          maxCharacters: 100
        },
        maidenName: {
          name: 'maidenName',
          title: 'Maiden Name',
          type: FormFieldType.text,
          placeholder: 'Maiden Name',
          value: data?.maidenName,
          required: false,
          hidden: !isPerson
        },
        companyName: {
          name: 'companyName',
          title: 'Company Name',
          type: FormFieldType.text,
          placeholder: 'Company Name',
          value: data?.companyName,
          required: isCompany,
          hidden: !isCompany,
          disabled: !isCompany,
          minCharacters: 2,
          maxCharacters: 300
        },
        initials: {
          name: 'initials',
          title: 'Initials',
          type: FormFieldType.text,
          placeholder: 'Initials',
          value: data?.initials,
          required: false
        },
        dateOfBirth: {
          name: 'dateOfBirth',
          title: 'Date of Birth',
          type: FormFieldType.date,
          placeholder: 'Date of Birth',
          value: data?.dateOfBirth,
          required: false,
          hidden: !isPerson,
          maxDate: new Date()
        },
        nationality: {
          name: 'nationality',
          title: 'Nationality',
          placeholder: 'Nationality',
          type: FormFieldType.select,
          value: data?.nationalityCode ? `_${data?.nationalityCode}` : null,
          selectOptions: lists?.nationalities,
          hidden: false,
          disabled: false
        },
        managerName: {
          name: 'managerName',
          title: 'Manager Name',
          type: FormFieldType.text,
          placeholder: 'Manager Name',
          value: data?.managerName,
          hidden: !isCompany
        }
      }
    },
    {
      sectionTitle: 'Contact Details',
      inputs: {
        email: {
          name: 'email',
          title: 'Email',
          type: FormFieldType.text,
          placeholder: 'Email',
          value: data?.email,
          required: false,
          disabled: !isEmpty(data?.userId),
          customValidator: (values) => {
            if (values.email && !REGEX.email.test(values.email)) {
              return 'Invalid Email Address';
            }

            return '';
          }
        },
        phoneType: {
          name: 'phoneType',
          title: 'Phone Type',
          type: FormFieldType.select,
          placeholder: 'Phone Type',
          value: data?.phoneType,
          required: true,
          selectOptions: lists?.phoneType
        },
        phoneNumber: {
          name: 'phoneNumber',
          title: 'Phone Number',
          type: FormFieldType.phoneNumber,
          placeholder: 'Mobile Number',
          value: data?.phoneNumber
        },
        relativeOf: {
          name: 'relativeOf',
          title: 'Relative Of',
          placeholder: 'Choose Related Person',
          type: FormFieldType.select,
          value: data?.relativeOfCode,
          selectOptions: filterPersonLovsById(
            lists?.activePersons,
            data?.personId
          ),
          required: false
        },
        relation: {
          name: 'relation',
          title: 'Relation',
          placeholder: 'Relation',
          type: FormFieldType.select,
          value: data?.relation,
          selectOptions: lists?.personRelation,
          required: false
        },
        mainBusinessPartner: {
          name: 'mainBusinessPartner',
          title: 'Main Business Partner',
          placeholder: 'Main Business Partner',
          type: FormFieldType.select,
          value: data?.mainBusinessPartner?.personId,
          selectOptions: filterPersonLovsById(
            lists?.activePersons,
            data?.personId
          ),
          disabled: true,
          required: false
        },
        introducedBy: {
          name: 'introducedBy',
          title: 'Introduced By',
          placeholder: 'Introduced By',
          type: FormFieldType.select,
          value: data?.introducedBy?.id,
          selectOptions: lists?.activePersons,
          disabled: true,
          required: false
        }
      }
    },
    {
      sectionTitle: 'Health Details',
      hidden: !isPerson,
      inputs: {
        smoker: {
          name: 'smoker',
          title: 'Smoker',
          placeholder: 'Smoker',
          type: FormFieldType.select,
          value: data?.smoker,
          selectOptions: { YES: 'Yes', NO: 'No' },
          hidden: !isPerson,
          disabled: !isPerson
        },
        weight: {
          name: 'weight',
          title: 'Weight (kg)',
          placeholder: 'Weight',
          type: FormFieldType.number,
          value: data?.weight,
          hidden: !isPerson,
          disabled: !isPerson
        },
        height: {
          name: 'height',
          title: 'Height (cm)',
          placeholder: 'Height',
          type: FormFieldType.number,
          value: data?.height,
          hidden: !isPerson,
          disabled: !isPerson
        },
        dateOfDeath: {
          name: 'dateOfDeath',
          title: 'Date of Death',
          placeholder: 'Date of Death',
          type: FormFieldType.date,
          value: data?.dateOfDeath,
          hidden: !isPerson,
          disabled: !isPerson,
          maxDate: new Date()
        },
        dateOfDeathNotified: {
          name: 'dateOfDeathNotified',
          title: 'Date of Death Notified',
          placeholder: 'Date of Death Notified',
          type: FormFieldType.date,
          value: data?.dateOfDeathNotified,
          hidden: !isPerson,
          disabled: !isPerson,
          maxDate: new Date()
        }
      }
    },
    {
      sectionTitle: 'Business Details',
      inputs: {
        taxable: {
          name: 'taxable',
          title: 'Taxable',
          type: FormFieldType.switch,
          value: data?.taxable,
          required: true
        },
        taxResidenceCountry: {
          name: 'taxResidenceCountry',
          title: 'Tax Residence Country',
          placeholder: 'Tax Residence Country',
          type: FormFieldType.select,
          value: data?.taxResidenceCountry,
          selectOptions: lists?.countries
        },
        financialReference: {
          name: 'financialReference',
          title: 'Financial Reference',
          placeholder: 'Financial Reference',
          type: FormFieldType.text,
          value: data?.financialReference
        },
        profession: {
          name: 'profession',
          title: 'Profession',
          placeholder: 'Profession',
          type: FormFieldType.select,
          value: data?.profession,
          selectOptions: lists?.occupationList ?? {},
          hidden: !isPerson,
          disabled: !isPerson
        },
        occupationClass: {
          name: 'occupationClass',
          title: 'Occupation Class',
          placeholder: 'Occupation Class',
          type: FormFieldType.select,
          value: data?.occupationClassCode,
          selectOptions: lists?.occupationClass ?? {},
          hidden: !isPerson,
          // Derived from the selected Profession; never user-editable.
          disabled: true
        },
        employer: {
          name: 'employer',
          title: 'Employer / Institute',
          placeholder: 'Employer',
          type: FormFieldType.text,
          value: data?.employer,
          hidden: !isPerson,
          disabled: !isPerson
        },
        dateOfEmployment: {
          name: 'dateOfEmployment',
          title: 'Date of Employment',
          placeholder: 'Date of Employment',
          type: FormFieldType.date,
          value: data?.dateOfEmployment,
          hidden: !isPerson,
          disabled: !isPerson
        },
        salary: {
          name: 'salary',
          title: 'Salary',
          placeholder: 'Salary',
          type: FormFieldType.currency,
          value: data?.salary,
          hidden: !isPerson,
          disabled: !isPerson,
          currencySymbol: '$'
        },
        dateOfSelfCertificateReceived: {
          name: 'dateOfSelfCertificateReceived',
          title: 'Date of Self Certificate Received',
          placeholder: 'Date of Self Certificate Received',
          type: FormFieldType.date,
          value: data?.dateOfSelfCertificateReceived,
          hidden: !isPerson,
          disabled: !isPerson
        },
        dateOfSelfCertificateValidated: {
          name: 'dateOfSelfCertificateValidated',
          title: 'Date of Self Certificate Validated',
          placeholder: 'Date of Self Certificate Validated',
          type: FormFieldType.date,
          value: data?.dateOfSelfCertificateValidated,
          hidden: !isPerson,
          disabled: !isPerson
        },
        category: {
          name: 'category',
          title: 'Category',
          placeholder: 'Category',
          type: FormFieldType.select,
          value: data?.category,
          selectOptions: lists?.personsCategories,
          hidden: true
        },
        dateOfKYC: {
          name: 'dateOfKYC',
          title: 'Date of KYC',
          placeholder: 'Date of KYC',
          type: FormFieldType.date,
          value: data?.dateOfKYC,
          hidden: !isPerson,
          disabled: !isPerson
        },
        comments: {
          name: 'comments',
          title: 'Comments',
          placeholder: 'Comments',
          type: FormFieldType.textArea,
          value: data?.comments
        },
        remarks: {
          name: 'remarks',
          title: 'Remarks',
          placeholder: 'Remarks',
          type: FormFieldType.textArea,
          value: data?.remarks
        },
        registrationPlace: {
          name: 'registrationPlace',
          title: 'Place of Registration',
          placeholder: 'Place of Registration',
          type: FormFieldType.select,
          value: data?.registrationPlace,
          selectOptions: lists?.countries,
          hidden: !isCompany,
          disabled: !isCompany
        },
        registrationDate: {
          name: 'registrationDate',
          title: 'Date of Registration',
          placeholder: 'Date of Registration',
          type: FormFieldType.date,
          value: data?.registrationDate,
          hidden: !isCompany,
          disabled: !isCompany,
          maxDate: new Date()
        }
      }
    },
    {
      sectionTitle: 'Legal Representative Information',
      hidden: isPerson,
      inputs: {
        legalRepresentativeFullName: {
          name: 'legalRepresentativeFullName',
          title: 'Full Name',
          placeholder: 'Full Name',
          type: FormFieldType.text,
          value: data?.legalRepresentativeFullName,
          hidden: !isCompany,
          disabled: !isCompany
        },
        legalRepresentativeDateOfBirth: {
          name: 'legalRepresentativeDateOfBirth',
          title: 'Date of Birth',
          placeholder: 'Date of Birth',
          type: FormFieldType.date,
          value: data?.legalRepresentativeDateOfBirth,
          hidden: !isCompany,
          disabled: !isCompany,
          maxDate: new Date()
        },
        legalRepresentativeNationality: {
          name: 'legalRepresentativeNationality',
          title: 'Nationality',
          placeholder: 'Nationality',
          type: FormFieldType.select,
          value: data?.legalRepresentativeNationality
            ? `_${data?.legalRepresentativeNationality}`
            : null,
          selectOptions: lists?.nationalities,
          hidden: !isCompany,
          disabled: !isCompany
        }
      }
    }
  ];
};
